"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetStarted() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mousePos, setMousePos] = useState({
    x: 0.5,
    y: 0.5
  });
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      setMousePos({
        x,
        y
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    let animationFrameId: number;
    const drawAbstractTexture = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      const offsetX = (mousePos.x - 0.5) * 20;
      const offsetY = (mousePos.y - 0.5) * 20;
      ctx.fillStyle = "hsla(200, 70%, 45%, 0.08)";
      for (let i = 0; i < 12; i++) {
        const x = w / 13 * (i + 1) + offsetX * (i % 2 === 0 ? 1 : -1);
        const y = h * 0.3 + Math.sin(i * 0.8) * 80 + offsetY;
        const radius = 60 + Math.sin(i * 1.2) * 20;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = "hsla(230, 80%, 40%, 0.06)";
      for (let i = 0; i < 10; i++) {
        const x = w / 11 * (i + 1) - offsetX * (i % 2 === 0 ? 0.8 : 1.2);
        const y = h * 0.7 + Math.cos(i * 0.6) * 60 - offsetY;
        const radius = 50 + Math.cos(i * 1.5) * 15;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      animationFrameId = requestAnimationFrame(drawAbstractTexture);
    };
    drawAbstractTexture();
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [mousePos]);
  return <section id="get-started" ref={ref} className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-dark-background text-foreground-dark py-20 lg:py-32">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 container mx-auto px-6 lg:px-8 max-w-4xl text-center">
        <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-lg lg:text-xl text-foreground-dark/90 mb-10 max-w-2xl mx-auto leading-relaxed">
            Take the first step toward compassionate, evidence-based care for your child. Our intake process is simple and designed to support you every step of the way.
          </p>

          <Link to="#intake-form">
            <Button size="lg" className="bg-accent text-foreground-accent px-10 py-6 text-lg font-semibold rounded-[var(--radius-md)] shadow-medium hover:bg-accent-hover hover:shadow-strong hover:scale-105 active:scale-95 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-visible:ring-4 focus-visible:ring-accent/40 animate-pulse-glow">
              Start Intake
            </Button>
          </Link>

          <p className="mt-8 text-sm text-foreground-dark/70">
            Questions?{" "}
            <Link to="/ContactPage" className="underline hover:text-secondary transition-colors duration-[var(--duration-fast)]">
              Contact us
            </Link>{" "}
            or call{" "}
            <Link to="tel:+13051234567" className="underline hover:text-secondary transition-colors duration-[var(--duration-fast)]">
              (305) 123-4567
            </Link>
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(72, 85, 201, 0.4);
          }
          50% {
            box-shadow: 0 0 30px rgba(72, 85, 201, 0.6);
          }
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </section>;
}